import { DialogTitle } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";

import { JSX } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "@/components/buttons/Button.tsx";
import Input from "@/components/inputs/Input.tsx";
import Textfield from "@/components/inputs/Textfield.tsx";

import { USER_KEYS, useOrderMovie } from "@/hooks/api/user.hooks";
import { useLang } from "@/hooks/useLang";
import useModalRouter from "@/hooks/useModalRouter";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

interface FormValues {
  content_name: string;
  content_link: string;
  content_description: string;
}

const OrderMovieModal = (): JSX.Element => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const { mutate: orderMovie, isPending } = useOrderMovie();
  const queryClient = useQueryClient();
  const lang = useLang();

  const { closeModal } = useModalRouter();

  const onSubmit: SubmitHandler<FormValues> = async (data): Promise<void> => {
    orderMovie(data, {
      onSuccess: () => {
        onSuccess(t("Orders.order-sent"));
        queryClient.invalidateQueries({ queryKey: USER_KEYS.movieOrders(lang) });
        closeModal();
      },
      onError,
    });
  };

  return (
    <div className={"max-w-[32.5rem]"}>
      <div className={"staff-modal__header text-center"}>
        <DialogTitle as="h3" className="staff-modal__title">
          {t("Orders.movie")}
        </DialogTitle>
        <p className="staff-modal__description grey">{t("Orders.move-message")}</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className={"mt-10 w-full space-y-10"}>
        <div className={"space-y-4"}>
          <Input
            type={"text"}
            color={"white"}
            name={"content_name"}
            placeholder={t("Orders.movie-name")}
            register={register}
            validation={{
              required: true,
            }}
            errors={errors}
          />
          <Input
            type={"text"}
            color={"white"}
            name={"content_link"}
            placeholder={t("Orders.movie-link")}
            register={register}
            validation={{
              required: true,
            }}
            errors={errors}
          />
          <Textfield
            color={"white"}
            name={"content_description"}
            placeholder={t("Orders.movie-description")}
            register={register}
            rows={6}
            className={"resize-none outline-transparent"}
          />
        </div>
        <Button isLoading={isPending} type={"submit"} aria-label={t("Actions.submit")}>
          {t("Actions.submit")}
        </Button>
      </form>
    </div>
  );
};

export default OrderMovieModal;
